/**
 * Symbol art for the Book of Dead-style slot. The 9 symbols are cropped at
 * runtime out of a single sheet (public/symboli-sve.jpg, 768x1024) using Pixi
 * texture frames — no per-symbol files needed. The provider is the source of
 * truth for which symbol lands; this only decides how it looks.
 *
 * Crop boxes frame each ornate tile (excluding the text label above it). They're
 * tuned by eye against the sheet; adjust here if a crop clips a neighbour.
 */
import { Assets, Texture, Rectangle } from 'pixi.js';
// Import the art through Vite so the emitted URLs resolve via the JS module
// location (import.meta.url) — robust regardless of the iframe mount path
// (e.g. /launch/). Referencing public/ by runtime path 404s under /launch/.
import sheetUrl from '../assets/symboli-sve-nove.jpg';
import bgUrl from '../assets/thumbnail.png';

export interface SymbolFrame { x: number; y: number; w: number; h: number; }

// Clean 3x3 grid of stone tiles in symboli-sve-nove.jpg (768x1024). Boxes are
// squared around each tile's detected centre so every symbol sits centered.
export const SYMBOL_FRAMES: Record<string, SymbolFrame> = {
  ten: { x: 23, y: 66, w: 228, h: 240 },
  jack: { x: 270, y: 67, w: 231, h: 239 },
  queen: { x: 518, y: 63, w: 235, h: 242 },
  king: { x: 21, y: 378, w: 229, h: 242 },
  ankh: { x: 266, y: 332, w: 240, h: 264 },
  scarab: { x: 518, y: 377, w: 233, h: 271 },
  anubis: { x: 19, y: 697, w: 233, h: 249 },
  explorer: { x: 266, y: 698, w: 239, h: 249 },
  book: { x: 521, y: 698, w: 230, h: 249 },
};

export const SYMBOL_IDS = Object.keys(SYMBOL_FRAMES);

// Accent colour per symbol — used for the winning-cell glow.
const ACCENTS: Record<string, number> = {
  ten: 0x9fb0ff,
  jack: 0x9fb0ff,
  queen: 0xc9a3ff,
  king: 0xffcf8a,
  ankh: 0x6ee0a0,
  scarab: 0x6ee0e0,
  anubis: 0xff8ab0,
  explorer: 0xffd45e,
  book: 0xffb24d,
};

export function accentFor(id: string): number {
  return ACCENTS[id] ?? 0xffffff;
}

export const BG_URL = bgUrl;

/** Load the sheet once and slice the 9 symbol sub-textures. */
export async function loadSymbolTextures(): Promise<Record<string, Texture>> {
  const sheet = await Assets.load<Texture>(sheetUrl);
  const out: Record<string, Texture> = {};
  for (const [id, f] of Object.entries(SYMBOL_FRAMES)) {
    out[id] = new Texture({ source: sheet.source, frame: new Rectangle(f.x, f.y, f.w, f.h) });
  }
  return out;
}
